import React, { FunctionComponent, LegacyRef, useEffect, useRef } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { Platform, TouchableOpacity, View, ViewStyle } from 'react-native'

import { isToday } from 'date-fns'
import { noop } from 'lodash-es'
import styled, { useTheme } from 'styled-components/native'

import { EssentialsMessageMetadata, MESSAGE_AUTHOR_TYPE } from '@lyrahealth-inc/shared-app-logic'

import { CalendarBookingIcon, ExerciseIcon, Tooltip } from '../../atoms'
import { BodyText, Size } from '../../atoms/bodyText/BodyText'
import { ChatBubble } from '../../atoms/chatBubble/ChatBubble'
import { ChatBubbleText } from '../../atoms/chatBubble/ChatBubbleText'
import { DeletedLiveMessageText } from '../../atoms/deletedLiveMessageText/DeletedLiveMessageText'
import { AuroraAICoachAvatarIcon } from '../../atoms/icons/AuroraAICoachAvatarIcon'
import { Avatar, avatarAltTextGeneric } from '../../atoms/icons/Avatar'
import { WarningIcon } from '../../atoms/icons/WarningIcon'
import { Timestamp } from '../../atoms/timestamp/Timestamp'
import { IS_WEB } from '../../constants'
import { useMediaQuerySize } from '../../hooks'
import { ActivityBubble } from '../../molecules/activityBubble/ActivityBubble'
import { Message, MessageAttachmentData, MessageType } from '../../ui-models'
import { ActivityReviewInfo } from '../../ui-models/assignments/Assignments'
import { ThemeType } from '../../utils'
import { decodeHtmlEntities, getFriendlyFormattedTimeStamp, tID } from '../../utils/utils'
import { EssentialsBubble } from '../essentialsBubble/EssentialsBubble'
import { MessageAttachmentRenderer } from '../messageAttachmentRenderer/MessageAttachmentRenderer'
import { MessageFeedback } from '../messageFeedback/MessageFeedback'

export type OnAiCoachFeedbackPressedType = (params: {
  value: string | null
  genAiMessageId: string
  feedbackResponseId?: string
}) => void

export type MessageItemProps = {
  item: Message
  onChatBubblePressed?: (messageId: string, receiver: boolean, activityInfo?: ActivityReviewInfo) => void
  onLinkPress?: (link: string) => void
  onAttachmentPressed?: (attachment: MessageAttachmentData, item: Message) => void
  onEssentialsPress?: (essentials: any) => void // TODO: fix types
  isAiCoachSelected?: boolean
  onAiCoachFeedbackPressed?: OnAiCoachFeedbackPressedType
  hideTimestampOnHover?: boolean
  dblAttachmentsDisabled?: boolean
}

const Row = styled.View<{ receiver: boolean }>(({ receiver }) => {
  const style = { marginVertical: '6px' }
  if (receiver) {
    return {
      ...style,
      flexDirection: 'row',
      flexWrap: 'nowrap',
    }
  } else {
    return {
      ...style,
      flexDirection: 'row-reverse',
    }
  }
})

const StatusContainer = styled.View({
  marginTop: '5px',
  marginRight: '5px',
  alignItems: 'flex-end',
})

const FailedStatus = styled.View({
  flexDirection: 'row',
})

const WarningIconContainer = styled.View({
  marginRight: '4px',
})

const AvatarContainer = styled.View({
  marginTop: '2px',
})

/**
 * This component renders a single message row which consists of the avatar, time stamp of when the message was created
 * and the chat bubble which contains the message itself.
 * Component also handles updating the status delivery of a message
 */
let MessageItem: FunctionComponent<MessageItemProps> = ({
  item,
  onChatBubblePressed,
  onLinkPress,
  onAttachmentPressed,
  onEssentialsPress,
  onAiCoachFeedbackPressed,
  isAiCoachSelected,
  hideTimestampOnHover = false,
  dblAttachmentsDisabled,
}) => {
  const intl = useIntl()
  const { colors, isAuroraTheme } = useTheme() as ThemeType
  const { isMobileSized } = useMediaQuerySize()
  const {
    chatBubbleReceiver,
    chatBubbleSender,
    chatBubbleReceiverAi,
    chatBubbleSenderAi,
    icon: messageItemIcon,
  } = colors.components.messageItem
  const messageRef = useRef<HTMLElement>(null)
  const {
    receiver,
    dateCreated,
    avatarDetails,
    message,
    sendStatus,
    messageId,
    showTime,
    linkText,
    activityResponseId,
    activityId,
    submitDate,
    activityTitle,
    type,
    messageType,
    attachments,
    essentials,
    authorType,
    messageFeedbackValue,
    genAiMessageId,
    feedbackResponseId,
    riskLevel,
  } = item

  const formattedDate = getFriendlyFormattedTimeStamp(intl, dateCreated)
  const dateCreatedObject = new Date(dateCreated)
  const locale = Intl.DateTimeFormat().resolvedOptions().locale
  const intlFormattedDateTime = new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
    hour12: true,
  }).format(dateCreatedObject)
  const intlFormattedTime = new Intl.DateTimeFormat(locale, {
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
    hour12: true,
  }).format(dateCreatedObject)
  const isAICoachMessage = (isAiCoachSelected && receiver) || authorType === MESSAGE_AUTHOR_TYPE.AI_COACH

  useEffect(() => {
    if (messageRef && messageRef.current && Platform.OS === 'web' && !hideTimestampOnHover) {
      messageRef.current.title = intlFormattedDateTime
    }
  }, [intlFormattedDateTime, hideTimestampOnHover])

  const chatBubblePosition: ViewStyle = {
    marginLeft: avatarDetails ? 15 : 0,
    // margin right below accounts for the avatar being shown next to message
    marginRight: receiver && avatarDetails ? 75 : isAICoachMessage ? 15 : 0,
    maxWidth: '75%',
  }

  // taken from ui-core. using regex to detect a url within a string
  const urlRegex = /(\b(?:https?|ftp|file):\/\/[-A-Z0-9+&@#/%?=~_|!:,.;$]*[-A-Z0-9+&@#/%=~_|])/gi
  const hasLinkInText = function (text: any) {
    const tokens = text.split(urlRegex)
    return tokens.length > 1
  }

  const failed = sendStatus === 'failed'
  const hasLink = hasLinkInText(message)
  // TODO: Remove the Platform Check after https://lyrahealth.atlassian.net/browse/BCDELIVERY-4707
  //  when it'll become interactive in mobile
  const isInteractive =
    Boolean(linkText) ||
    (Boolean(activityResponseId) && Boolean(activityId)) ||
    (messageType && ['session_cancellation', 'request_session_time'].includes(messageType) && Platform.OS === 'web')
  const chatBubbleTouchable =
    (!receiver && failed) ||
    (receiver && !failed && isInteractive) ||
    Boolean(activityId) ||
    (messageType && ['session_cancellation', 'request_session_time'].includes(messageType) && Platform.OS === 'web') ||
    hasLink

  // Define chat bubble colors - Aurora uses uniform transparent white overlay for all bubbles
  const auroraChatColors = {
    backgroundColor: colors.components.messageItem.chatBubbleAurora.background,
    borderColor: 'transparent',
    fontColor: colors.components.messageItem.chatBubbleAurora.text,
  }

  const chatColorConfig = {
    receiver: {
      aiCoachConversation: {
        backgroundColor: [chatBubbleReceiverAi.gradientStart.background, chatBubbleReceiverAi.gradientEnd.background],
        borderColor: chatBubbleReceiverAi.border,
        fontColor: chatBubbleReceiverAi.text,
      },
      providerConversation: {
        backgroundColor: chatBubbleReceiver.background,
        borderColor: chatBubbleReceiver.border,
        fontColor: colors.textPrimary,
      },
    },
    sender: {
      aiCoachConversation: {
        backgroundColor: chatBubbleSenderAi.background,
        borderColor: chatBubbleSenderAi.border,
        fontColor: chatBubbleSenderAi.text,
      },
      providerConversation: {
        backgroundColor: chatBubbleSender.background,
        borderColor: chatBubbleSender.border,
        fontColor: colors.textInverse,
      },
    },
  }

  const { backgroundColor, borderColor, fontColor } = isAuroraTheme
    ? auroraChatColors
    : chatColorConfig[receiver ? 'receiver' : 'sender'][
        isAiCoachSelected ? 'aiCoachConversation' : 'providerConversation'
      ]

  const onPress = () => {
    onChatBubblePressed && chatBubbleTouchable
      ? isInteractive && !hasLink
        ? onChatBubblePressed(messageId, receiver, {
            id: activityId,
            responseId: activityResponseId,
            activityType: messageType,
          })
        : onChatBubblePressed(messageId, receiver)
      : noop()
  }

  const icon =
    messageType === 'request_session_time' ? (
      <CalendarBookingIcon fillColor={!receiver ? colors.iconInverse : messageItemIcon.receiver.fill} />
    ) : (
      <ExerciseIcon fillColor={!receiver ? colors.iconInverse : messageItemIcon.receiver.fill} />
    )

  const renderEssentialsContent = (essentials: any) => {
    return essentials.map((essential: EssentialsMessageMetadata) => {
      return (
        <EssentialsBubble
          key={essential.uri}
          title={essential.title}
          url={essential.uri}
          thumbnailURI={essential.thumbnail}
          articleTypeLabel={essential.articleTypeLabel}
          readTime={essential.readTime}
          onEssentialsPress={(url) => onEssentialsPress && onEssentialsPress(url)}
        />
      )
    })
  }

  return (
    // casting to unknown because TS says View and HTMLElements do not have enough overlap
    //   if we don't cast to unknown, we'll need a tsignore when we set the ref's index property
    <View testID={tID('MessageItem')} ref={messageRef as unknown as LegacyRef<View>}>
      {showTime ? (
        <View testID={tID('MessageItem-timestamp')}>
          <Timestamp time={formattedDate} />
        </View>
      ) : null}
      {type === MessageType.DELETED ? (
        <DeletedLiveMessageText />
      ) : (
        <Row receiver={receiver}>
          {avatarDetails && (
            <AvatarContainer testID={tID('Messages-avatar')}>
              {isAICoachMessage && isAuroraTheme ? (
                <AuroraAICoachAvatarIcon size={48} />
              ) : (
                <Avatar
                  details={avatarDetails}
                  accessibilityLabel={intl.formatMessage(avatarAltTextGeneric, { name: avatarDetails.displayName })}
                  size={32}
                />
              )}
            </AvatarContainer>
          )}
          <View style={chatBubblePosition}>
            <TouchableOpacity onPress={onPress} disabled={!chatBubbleTouchable}>
              {/* If the message is a comment (feedback) from the provider about an exercise */}
              {type === MessageType.ACTIVITY || messageType === 'request_session_time' ? (
                <ActivityBubble
                  title={activityTitle}
                  backgroundColor={backgroundColor}
                  activityText={message}
                  submitDate={submitDate}
                  fontColor={!receiver ? colors.textInverse : colors.textPrimary}
                  icon={icon}
                />
              ) : IS_WEB && !isMobileSized && !hideTimestampOnHover ? (
                <Tooltip
                  content={isToday(dateCreatedObject) ? intlFormattedTime : intlFormattedDateTime}
                  placement='top'
                  hoverEnabled
                  style={{ width: '100%' }}
                >
                  <ChatBubble
                    body={
                      <ChatBubbleText
                        text={decodeHtmlEntities(message)}
                        fontColor={fontColor}
                        onLinkPress={onLinkPress}
                      />
                    }
                    backgroundColor={backgroundColor}
                    borderColor={borderColor}
                    messageType={messageType}
                  />
                </Tooltip>
              ) : (
                <ChatBubble
                  body={
                    <ChatBubbleText
                      text={decodeHtmlEntities(message)}
                      fontColor={fontColor}
                      onLinkPress={onLinkPress}
                    />
                  }
                  backgroundColor={backgroundColor}
                  borderColor={borderColor}
                  messageType={messageType}
                />
              )}
              {essentials?.length ? renderEssentialsContent(essentials) : null}
              {isAICoachMessage && onAiCoachFeedbackPressed && genAiMessageId && !riskLevel && (
                <MessageFeedback
                  onPress={(value) => onAiCoachFeedbackPressed({ value, genAiMessageId, feedbackResponseId })}
                  value={messageFeedbackValue}
                />
              )}
            </TouchableOpacity>
          </View>
        </Row>
      )}
      {attachments?.map((attachment) => (
        <Row key={attachment.key} receiver={receiver}>
          <View style={chatBubblePosition} testID={tID(`MessageItem-attachment-${attachment.key}`)}>
            <MessageAttachmentRenderer
              attachment={attachment}
              onPress={() => {
                onAttachmentPressed && onAttachmentPressed(attachment, item)
              }}
              dblAttachmentsDisabled={dblAttachmentsDisabled}
            />
          </View>
        </Row>
      ))}
      {sendStatus !== 'sent' ? (
        <StatusContainer testID={tID('MessageItem-status')}>
          {!receiver && sendStatus === 'sending' ? (
            <BodyText
              text={
                <FormattedMessage
                  defaultMessage='SENDING...'
                  description='This is a label that tells the user this message in the process of being sent'
                />
              }
              size={Size.CAPTION}
              textAlign='right'
              color={colors.textSecondary}
            />
          ) : null}
          {!receiver && sendStatus === 'failed' ? (
            <FailedStatus>
              <WarningIconContainer>
                <WarningIcon size={16} fillColor={colors.iconError} />
              </WarningIconContainer>
              <BodyText
                text={
                  <FormattedMessage
                    defaultMessage='NOT DELIVERED'
                    description='This is a label that tells the user this message did not reach the server and was not delivered'
                  />
                }
                size={Size.CAPTION}
                textAlign='right'
                color={colors.textError}
              />
            </FailedStatus>
          ) : null}
        </StatusContainer>
      ) : null}
    </View>
  )
}

MessageItem = React.memo(MessageItem)

export { MessageItem }
